import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearParticipantsLastAndNextAppointments,
    searchParticipantsLastAndNextAppointments
} from '../../../store/calendarService';
import { clearParticipantsSearch, searchParticipants } from '../../../store/participantService';
import { selectSortedTeamsForLoggedInUser } from '../../../store/userSelectors';
import { searchTeamsForLoggedInUser } from '../../../store/userService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { clearKeys } from '../../../utils/objectUtils';
import { stableSort } from '../../../utils/sortFunctions';
import { addFullNameToArray } from '../../../utils/userArrayUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import SingleSelect from '../../formElements/SingleSelect';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import AppointmentTableRow from './AppointmentTableRow';

import local from '../homeStyles/futureAppointments.module.css';

const headCells = [
    { id: 'ptCode', label: 'ID' },
    { id: 'name', label: 'Name' },
    { id: 'participantStatus', label: 'Status' },
    { id: 'lastAppointment', label: 'Last Appointment' },
    { id: 'attendanceStatus', label: 'Status' },
    { id: 'lastAppointmentDate', label: 'Date' },
    { id: 'nextAppointment', label: 'Next Appointment' },
    { id: 'nextAppointmentDate', label: 'Date' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'ptCode',
    page: 0,
    rowsPerPage: 100
};

const acceptedRoles = [ADVISER, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const adviserRoles = [ADVISER, MANAGER];
const excludedStatuses = [
    'Employed',
    'Closed',
    'Early Completer',
    'Complete',
    'Completer',
    'Early Leaver',
    'Did not Engage',
    'Disengaged',
    'Dormant',
    'On Hold',
    'Planned Pause'
];

const FutureAppointmentManagement = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [advisers, setAdvisers] = useState([]);
    const [teamIds, setTeamIds] = useState([]);
    const [adviserId, setAdviserId] = useState('');
    const [participants, setParticipants] = useState([]);
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [keys, setKeys] = useState({ teamIds: '0', adviserId: '1' });
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const teamDetails = useSelector(selectSortedTeamsForLoggedInUser);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const participantsByAdviser = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const lastAndNextAppointments = useSelector(
        (state) => state.entities.calendarService.participantsLastAndNextAppointments
    );

    // USEEFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        if (Object.keys(loggedInUser).length < 1 || !('serviceIds' in loggedInUser)) return;
        loggedInUser?.teamIds.length > 0 &&
            teamDetails?.length < 1 &&
            dispatch(searchTeamsForLoggedInUser({ ids: loggedInUser.teamIds }));
    }, [loggedInUser]);

    useEffect(() => {
        if (
            participantsByAdviser?.length > 0 &&
            'number' in participantsSearchMetaData &&
            !participantsSearchMetaData.last
        ) {
            dispatch(searchParticipants({ adviserId }, participantsSearchMetaData.number + 1));
        } else if (participantsSearchMetaData.last && participantsByAdviser?.length > 0) {
            const newParticipants = participantsByAdviser.filter(
                (el) => !excludedStatuses.includes(el.status)
            );
            if (newParticipants.length > 0) {
                const participantIds = newParticipants.map((el) => el.id);
                setParticipants(newParticipants);
                dispatch(searchParticipantsLastAndNextAppointments(participantIds));
            }
        }
    }, [participantsByAdviser, participantsSearchMetaData]);

    useEffect(() => {
        if (Object.keys(lastAndNextAppointments)?.length < 1) setRows([]);
        const newRows = participants.map((el) => {
            return {
                id: el.id,
                ptCode: el.ptCode,
                name: `${el.firstName || ''} ${el.lastName || ''}`,
                participantStatus: el.status,
                lastAppointment: lastAndNextAppointments[el.id]?.lastAppointment?.type || '',
                attendanceStatus: lastAndNextAppointments[el.id]?.lastAppointment?.status || '',
                lastAppointmentDate: lastAndNextAppointments[el.id]?.lastAppointment?.date
                    ? reverseFormatDate(lastAndNextAppointments[el.id]?.lastAppointment?.date)
                    : '',
                nextAppointment: lastAndNextAppointments[el.id]?.nextAppointment?.type || '',
                nextAppointmentDate: lastAndNextAppointments[el.id]?.nextAppointment?.date
                    ? reverseFormatDate(lastAndNextAppointments[el.id]?.nextAppointment?.date)
                    : ''
            };
        });
        setRows(newRows);
    }, [lastAndNextAppointments]);

    useEffect(() => {
        !isSubmitted && setIsButtonDisabled(!adviserId);
    }, [adviserId]);

    // FNS
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <AppointmentTableRow key={el.id} row={el} />
        ));

    const cleanUp = () => {
        dispatch(clearParticipantsSearch());
        setKeys(clearKeys(keys));
        setAdvisers([]);
        setTeamIds([]);
        setAdviserId('');
        setRows([]);
    };

    // EVENT HANDLERS
    const onUpdateTeams = (chosenIds) => {
        rows.length > 0 && setRows([]);
        setTeamIds(chosenIds);
        if (chosenIds.length < 1) setAdvisers([]);
        setAdviserId('');
        const updatedAdvisers = users.filter(
            (el) =>
                [el.primaryTeam?.id, ...el.otherTeams.map((entry) => entry.id)].find((entry) =>
                    chosenIds.includes(entry)
                ) && el.userTypes.find((entry) => adviserRoles.includes(entry.role))
        );
        setAdvisers(addFullNameToArray(updatedAdvisers));
    };

    const onUpdateAdviser = (chosenId) => {
        setRows([]);
        setParticipants([]);
        dispatch(clearParticipantsLastAndNextAppointments());
        isSubmitted && setIsSubmitted(false);
        setAdviserId(chosenId);
    };

    const onSearch = (e) => {
        e.preventDefault();
        dispatch(searchParticipants({ adviserId }, 0));
        setIsButtonDisabled(true);
        setIsSubmitted(true);
    };

    // RENDER
    return (
        <div className={local.container}>
            <div className={local.toolBar} data-testid="future-appointments-toolbar">
                <MultiSelect
                    id="teamIds"
                    key={keys.teamIds}
                    label="Teams"
                    placeholder="Select team(s)..."
                    disabled={!hasRole(acceptedRoles, roles)}
                    menuItems={teamDetails || []}
                    preSelectedIds={teamIds || []}
                    preSelects={teamDetails.filter((el) => teamIds.includes(el.id)) || []}
                    onChange={(chosenIds) => onUpdateTeams(chosenIds)}
                />
                <SingleSelect
                    id="adviserId"
                    key={keys.adviserId}
                    label="Adviser"
                    placeholder="Select an adviser..."
                    disabled={!hasRole(acceptedRoles, roles) || teamIds.length < 1}
                    customClass="absolutePosition"
                    menuItems={advisers || []}
                    selectedId={adviserId || ''}
                    selected={advisers.find((el) => el.id === adviserId) || {}}
                    onChange={(chosenId) => onUpdateAdviser(chosenId)}
                />

                <span className={local.button}>
                    <Button
                        id="futureAppointmentsUpdateButton"
                        type="button"
                        disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                        content="Update"
                        onClick={onSearch}
                    />
                </span>
            </div>
            <div>
                {!adviserId || rows.length < 1 ? (
                    teamIds.length < 1 || !adviserId || !isButtonDisabled ? (
                        'Select team(s) and an adviser and Update'
                    ) : (
                        <LoadingSpinner content="No appointments found" time={1} />
                    )
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        defaultRowsPerPage={rowMetaData.rowsPerPage}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
        </div>
    );
};

export default FutureAppointmentManagement;
